import React, {FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";

export type ThemeMode = "light" | "dark" | "system";

type AssistantThemeContextType = {
    themeMode: ThemeMode;
    setThemeMode: (mode: ThemeMode) => void;
    resolvedTheme: "light" | "dark";
};

const STORAGE_KEY = "assistant-theme-mode";

const AssistantThemeContext = createContext<AssistantThemeContextType>({
    themeMode: "system",
    setThemeMode: () => undefined,
    resolvedTheme: "light",
});

function getSystemPreference(): "light" | "dark" {
    if (typeof window === "undefined" || !window.matchMedia) {
        return "light";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredThemeMode(): ThemeMode {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "light" || stored === "dark" || stored === "system") {
            return stored;
        }
    } catch {
        // localStorage not available
    }
    return "system";
}

type AssistantThemeProviderProps = {
    children: ReactNode;
};

export const AssistantThemeProvider: FC<AssistantThemeProviderProps> = ({children}) => {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredThemeMode);
    const [systemPref, setSystemPref] = useState<"light" | "dark">(getSystemPreference);

    const setThemeMode = useCallback((mode: ThemeMode) => {
        setThemeModeState(mode);
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // localStorage not available
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) {
            return;
        }
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e: MediaQueryListEvent) => {
            setSystemPref(e.matches ? "dark" : "light");
        };
        // Safari <14 uses addListener instead of addEventListener
        if (mediaQuery.addListener) {
            mediaQuery.addListener(handler as any);
        } else if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handler);
        }
        return () => {
            if (mediaQuery.removeListener) {
                mediaQuery.removeListener(handler as any);
            } else if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", handler);
            }
        };
    }, []);

    const resolvedTheme = useMemo<"light" | "dark">(() => {
        if (themeMode === "system") {
            return systemPref;
        }
        return themeMode;
    }, [themeMode, systemPref]);

    const value = useMemo<AssistantThemeContextType>(
        () => ({themeMode, setThemeMode, resolvedTheme}),
        [themeMode, setThemeMode, resolvedTheme],
    );

    return <AssistantThemeContext.Provider value={value}>{children}</AssistantThemeContext.Provider>;
};

export const useAssistantTheme = (): AssistantThemeContextType => {
    return useContext(AssistantThemeContext);
};
